import { CheckCircle2, Clock, Pencil } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/Shared/Card';
import { Button } from '@/components/Shared/Button';
import { EmptyState } from '@/components/Shared/EmptyState';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/Shared/Table';
import { useTimeclockStore, type TimeClockFilterKey } from '@/store/useTimeclockStore';
import type { FacilityFlowCoreModulesPlanningWorkflowsTimeClockTimeClockDayDto as TimeClockDay } from '@/api/generated/models';
import { formatTimeSpan } from '@/utils/timeclockTotals';
import { WorkTimeWithCredit } from './WorkTimeWithCredit';
import { DayStatusBadge, EventBadges } from './EventBadges';
import { canConfirm, formatIsoDay, type DescribeChoices } from './timeClockFormat';

const FILTER_KEYS: TimeClockFilterKey[] = ['open', 'thisWeek', 'thisMonth', 'lastMonth'];

interface TimeClockDaysTabProps {
  describeChoices: DescribeChoices;
  canEditEntry: (entry: TimeClockDay) => boolean;
  onEdit: (entry: TimeClockDay) => void;
}

/** Tab "Tage" of the time clock: the stamped days by filter, with correction and confirmation. */
export function TimeClockDaysTab({ describeChoices, canEditEntry, onEdit }: TimeClockDaysTabProps) {
  const { t } = useTranslation('timeclock');
  const { historyEntries, currentFilter, error, fetchHistory, confirmDay, clearError } = useTimeclockStore();

  return (
    <Card title={t('history.title')}>
      <div className="flex flex-wrap gap-2 pb-4">
        {FILTER_KEYS.map((key) => (
          <Button
            key={key}
            variant={currentFilter === key ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => fetchHistory(key)}
          >
            {t(`history.filters.${key}`)}
          </Button>
        ))}
      </div>

      {error ? (
        <EmptyState
          variant="error"
          title={t('errors.loadTitle')}
          description={error ?? t('errors.loadDescription')}
          onRetry={() => {
            clearError();
            fetchHistory(currentFilter);
          }}
        />
      ) : historyEntries.length === 0 ? (
        <EmptyState
          icon={<Clock className="w-12 h-12" strokeWidth={1.75} />}
          title={t('history.empty.title')}
          description={t('history.empty.description')}
        />
      ) : (
        <Table>
          <TableHead>
            <TableRow>
              <TableHeader>{t('table.date')}</TableHeader>
              <TableHeader className="hidden sm:table-cell">{t('table.events')}</TableHeader>
              <TableHeader className="hidden md:table-cell">{t('table.workTime')}</TableHeader>
              <TableHeader className="hidden md:table-cell">{t('table.breakTime')}</TableHeader>
              <TableHeader>{t('table.status')}</TableHeader>
              <TableHeader>{t('table.actions')}</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {historyEntries.map((entry) => (
              <TableRow key={entry.timeClockDayId}>
                <TableCell>
                  <div className="text-[13px] font-medium text-slate-900 tabular-nums">{formatIsoDay(entry.date)}</div>
                  <div className="sm:hidden mt-1">
                    <EventBadges events={entry.events} compact t={t} describeChoices={describeChoices} />
                  </div>
                </TableCell>
                <TableCell className="hidden sm:table-cell">
                  <EventBadges events={entry.events} t={t} describeChoices={describeChoices} />
                </TableCell>
                <TableCell className="hidden md:table-cell tabular-nums">
                  <WorkTimeWithCredit entry={entry} />
                </TableCell>
                <TableCell className="hidden md:table-cell tabular-nums">
                  {formatTimeSpan(entry.totalBreakTime)}
                </TableCell>
                <TableCell>
                  <DayStatusBadge status={entry.status} t={t} />
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1 sm:gap-2">
                    {canEditEntry(entry) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onEdit(entry)}
                        title={t('buttons.edit')}
                        aria-label={t('buttons.edit')}
                        className="p-1.5"
                      >
                        <Pencil className="w-4 h-4" strokeWidth={2} />
                      </Button>
                    )}
                    {canConfirm(entry) ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => confirmDay(entry.date ?? '')}
                        title={t('buttons.confirm')}
                        aria-label={t('buttons.confirm')}
                        className="p-1.5"
                      >
                        <CheckCircle2 className="w-4 h-4 text-emerald-600" strokeWidth={2} />
                      </Button>
                    ) : !canEditEntry(entry) ? (
                      <span className="text-[12px] text-slate-400">–</span>
                    ) : null}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Card>
  );
}
